import { useCallback, useEffect, useState } from "react";
import { CheckCircle2, ScrollText, XCircle } from "lucide-react";
import { api } from "../../lib/api";
import { fmtDate } from "../../lib/format";

const SOURCE_LABELS = { payment: "Ödeme", marketplace: "Pazaryeri", supplier: "Tedarikçi", invoice: "Fatura", email: "E-posta" };
const selectCls = "h-9 px-3 bg-[#0F1115] border border-[#272B33] rounded-lg text-xs text-neutral-300 focus:outline-none focus:border-blue-500";

export default function IntegrationLogsPage() {
  const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 1, providers: [] });
  const [source, setSource] = useState("");
  const [provider, setProvider] = useState("");
  const [okFilter, setOkFilter] = useState("");
  const [page, setPage] = useState(1);

  const load = useCallback(async () => {
    const params = new URLSearchParams({ page: String(page), limit: "50" });
    if (source) params.set("source", source);
    if (provider) params.set("provider", provider);
    if (okFilter !== "") params.set("ok", okFilter);
    const { data } = await api.get(`/admin/integration-logs?${params}`);
    setData(data);
  }, [page, source, provider, okFilter]);
  useEffect(() => { load().catch(() => {}); }, [load]);

  return (
    <div className="space-y-5" data-testid="admin-integration-logs">
      <div>
        <h1 className="font-display font-extrabold text-2xl text-white flex items-center gap-2"><ScrollText size={20} className="text-blue-400" /> Entegrasyon Logları</h1>
        <p className="text-xs text-neutral-500 mt-1">Tüm harici API işlemleri — API anahtarı, şifre ve hassas bilgiler loglarda asla gösterilmez.</p>
      </div>

      <div className="flex gap-2 flex-wrap">
        <select value={source} onChange={(e) => { setSource(e.target.value); setPage(1); }} className={selectCls} data-testid="ilog-source-filter">
          <option value="">Tüm Kaynaklar</option>
          {Object.entries(SOURCE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select value={provider} onChange={(e) => { setProvider(e.target.value); setPage(1); }} className={selectCls} data-testid="ilog-provider-filter">
          <option value="">Tüm Sağlayıcılar</option>
          {data.providers.map((p) => <option key={p} value={p} className="capitalize">{p}</option>)}
        </select>
        <select value={okFilter} onChange={(e) => { setOkFilter(e.target.value); setPage(1); }} className={selectCls} data-testid="ilog-status-filter">
          <option value="">Tümü</option>
          <option value="true">Başarılı</option>
          <option value="false">Başarısız</option>
        </select>
      </div>

      <div className="bg-[#181B20] border border-[#272B33] rounded-2xl overflow-hidden">
        {data.items.map((l) => (
          <div key={l.log_id} className="flex items-center justify-between gap-4 px-5 py-3.5 border-b border-[#272B33]/50 last:border-0" data-testid={`ilog-row-${l.log_id}`}>
            <div className="flex items-center gap-3 min-w-0">
              {l.ok ? <CheckCircle2 size={16} className="text-emerald-400 shrink-0" /> : <XCircle size={16} className="text-rose-400 shrink-0" />}
              <div className="min-w-0">
                <p className="text-sm text-white">
                  <span className="text-neutral-500 text-[11px] uppercase mr-2">{SOURCE_LABELS[l.source] || l.source}</span>
                  <span className="capitalize font-semibold">{l.provider}</span>
                  <span className="text-neutral-400"> · {l.operation}</span>
                </p>
                {l.message && <p className={`text-[11px] mt-0.5 truncate max-w-xl ${l.ok ? "text-neutral-500" : "text-rose-400/90"}`} title={l.message}>{l.message}</p>}
              </div>
            </div>
            <div className="text-right shrink-0">
              <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full ${l.ok ? "bg-emerald-500/15 text-emerald-400" : "bg-rose-500/15 text-rose-400"}`}>
                {l.ok ? "BAŞARILI" : "BAŞARISIZ"}
              </span>
              <p className="text-[10px] text-neutral-600 mt-1 font-mono-ed">{fmtDate(l.created_at)}</p>
            </div>
          </div>
        ))}
        {data.items.length === 0 && <p className="text-center text-neutral-500 text-sm py-12">Kayıt yok</p>}
      </div>

      {data.pages > 1 && (
        <div className="flex justify-center gap-2">
          {Array.from({ length: data.pages }).map((_, i) => (
            <button key={i} onClick={() => setPage(i + 1)}
              className={`w-9 h-9 rounded-lg text-xs font-semibold ${page === i + 1 ? "bg-blue-600 text-white" : "bg-[#181B20] border border-[#272B33] text-neutral-400"}`}
              data-testid={`ilog-page-${i + 1}`}>{i + 1}</button>
          ))}
        </div>
      )}
    </div>
  );
}
